import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { BehaviorSubject, Observable } from 'rxjs';

export type StorageKey =
  | 'token'
  | 'plan'
  | 'name'
  | 'diet_journey'
  | 'sign_up_value'
  | 'currency'
  | 'applied_discount'
  | 'last_water_intake'
  | 'last_sleep_log'
  | 'last_mood_log';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private _storage: Storage | null = null;
  private _storageReady: Promise<void>;

  // Reactive Subject for real-time syncing between Home & Water Log
  private waterIntakeSubject = new BehaviorSubject<any>(null);
  public waterIntake$: Observable<any> = this.waterIntakeSubject.asObservable();

  // Reactive Subject for real-time syncing between Home & Sleep Log
  private sleepLogSubject = new BehaviorSubject<any>(null);
  public sleepLog$: Observable<any> = this.sleepLogSubject.asObservable();

  constructor(private storage: Storage) {
    this._storageReady = this.init();
  }

  async init() {
    const storage = await this.storage.create();
    this._storage = storage;

    // Load initial stored water value on app boot
    const initialWater = await this._storage.get('last_water_intake');
    if (initialWater) {
      this.waterIntakeSubject.next(initialWater);
    }

    // Load initial stored sleep value on app boot
    const initialSleep = await this._storage.get('last_sleep_log');
    if (initialSleep) {
      this.sleepLogSubject.next(initialSleep);
    }
  }

  private async ensureReady() {
    await this._storageReady;
  }

  public async set(key: StorageKey, value: any) {
    await this.ensureReady();
    await this._storage?.set(key, value);

    // Broadcast live updates to subscribers
    if (key === 'last_water_intake') {
      this.waterIntakeSubject.next(value);
    } else if (key === 'last_sleep_log') {
      this.sleepLogSubject.next(value);
    }
  }

  public async get(key: StorageKey): Promise<any> {
    await this.ensureReady();
    return await this._storage?.get(key);
  }

  public async remove(key: StorageKey) {
    await this.ensureReady();
    await this._storage?.remove(key);
  }

  public async clear() {
    await this.ensureReady();
    await this._storage?.clear();
  }
}